import { useContext, useMemo, useReducer } from 'react'
import { AuthContext, AuthReducer, AuthState, initState } from './reducer'

// TYPES
import type { IToast } from '@components/molecules/toast'
import { type AuthScreen, authScreens } from '../'

// UTILITIES
import * as AuthActions from './actions'
import { useNavigate } from 'react-router-dom'


/**
 * Instantiates a new `Auth` store and returns the state and dispatch in an object for use in an `AuthContext.Provider`.\
 * Only one Reducer is needed per Provider, and as such, this hook should be used as close to it's designated Provider as possible.\
 * ### Use the `useAuthState` and `useAuthDispatch` hooks to access Reducer data.
 * 
 * @returns A pre-formatted object an AuthProvider will accept as a value containing the Reducer's State and Dispatch.
 */
export const useAuthReducer = () => {
    const [localState, localDispatch] = useReducer(AuthReducer, initState)
    return { localState, localDispatch }
}

/**
 * Access the nearest `Auth` Provider and returns the full state.
 * As a safe guard, an error is thrown if a Provider is not found.
 * 
 * @returns The full `AuthState` object.
 */
export const useAuthState = () => {
    const localState = useContext(AuthContext).localState
    if (!localState) throw new Error('useAuthState must be used within an AuthContext Provider')
    return localState
}

/**
 * Access the nearest `Auth` Provider and returns it's Dispatch method.
 * As a safe guard, an error is thrown if a Provider is not found.
 * 
 * @returns A Dispatch method that accepts `AuthActionTypes` for the purposes of updating the Auth state.
 */
export const useAuthDispatch = () => {
    const localDispatch = useContext(AuthContext).localDispatch
    if (!localDispatch) throw new Error('useAuthDispatch must be used within an AuthContext Provider')
    return localDispatch
}


type AuthSettings = () => {
    /**
     * A quick way of appending data to the `toast` variable in the Auth store.
     *
     * @param props Accepts either a `string` or `IToast` arguments. By default, if typeof string, the Toast displayed will be of type error.
     * @returns 
     */
    createToast: (props: string | IToast) => void

    /**
     * Controls the `isNewUser` bool in the Auth Store
     * 
     * @param isNewUser
     * @returns 
     */
    setIsNewUser: (isNewUser: boolean) => void

    /**
     * Controls the `currentScreen` and `prevScreen` variables in the Auth store.
     * Based off of the `transitionTime` in the store, the screens will animate in and out.
     * 
     * @param toScreen Any valid `AuthScreen`
     * @returns 
     */
    setScreen: (toScreen: AuthScreen) => void

    /**
     * A copy of the state in the local AuthReducer.
     */
    state: AuthState
}

/**
 * Returns an object of pre-formatted utilities that control certain aspects of the Auth process.
 */
export const useAuthSettings: AuthSettings = () => {
    const state = useAuthState()
    const { transitionTime, currentScreen } = state
    const localDispatch = useAuthDispatch()

    function setIsNewUser(isNewUser: boolean) {
        localDispatch(AuthActions.setIsNewUser(isNewUser))
    }

    /**
     * Used to keep track of the AuthScreens order using their respective indexes in the array.
     */
    const authScreenOrder: Record<AuthScreen, number> = useMemo(() => authScreens.reduce((prev, next, index) => ({
        ...prev,
        [next]: index
    }), {} as Record<AuthScreen, number>), [authScreens])

    const navigate = useNavigate()
    function setScreen(toScreen: AuthScreen) {
        if (currentScreen === toScreen) return

        if (transitionTime === 0) {
            // A zero transition time doesn't need any additional state dispatches and should skip right to the `toScreen`
            localDispatch(AuthActions.setTransitionState("visible"))
            localDispatch(AuthActions.setScreen(toScreen))
            navigate(`/${toScreen}${document.location.search}`)
        } else {
            const direction = authScreenOrder[currentScreen] < authScreenOrder[toScreen] ? "right" : "left"
            if (direction === 'right') {
                localDispatch(AuthActions.setTransitionState("slide-out-left"))
                setTimeout(() => {
                    localDispatch(AuthActions.setTransitionState("hidden"))
                    localDispatch(AuthActions.setScreen(toScreen))
                    setTimeout(() => {
                        localDispatch(AuthActions.setTransitionState("slide-in-right"))
                        setTimeout(() => {
                            localDispatch(AuthActions.setTransitionState("visible"))
                        }, transitionTime)
                    }, transitionTime)
                }, transitionTime)
            } else if (direction === 'left') {
                localDispatch(AuthActions.setTransitionState("slide-out-right"))
                setTimeout(() => {
                    localDispatch(AuthActions.setTransitionState("hidden"))
                    localDispatch(AuthActions.setScreen(toScreen))
                    setTimeout(() => {
                        localDispatch(AuthActions.setTransitionState("slide-in-left"))
                        setTimeout(() => {
                            localDispatch(AuthActions.setTransitionState("visible"))
                        }, transitionTime)
                    }, transitionTime)
                }, transitionTime)
            }
        }
    }

    function createToast(props: string | IToast) {
        localDispatch(AuthActions.setToast(typeof props === 'string' ? {
            type: 'error',
            text: props,
        } : props))
    }

    return { createToast, setIsNewUser, setScreen, state }
}
